import React, { useState } from 'react';
import { FiFileText, FiSearch, FiX, FiPlus, FiPrinter, FiDownload } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SalesSidebar from '../components/SalesSidebar';
import GenericAddModal from '../components/GenericAddModal';
import useLocalState from '../hooks/useLocalState';
import { sortData, getNextStatus } from '../utils/tableUtils';

const initialData = [
  { ref: 'FA2605-0041', third: 'WONG CHING YEE', date: '05/20/2026', due: '05/21/2026', amtExcl: '2,500.00', amtInc: '2,500.00', paid: '2,500.00', remain: '0.00', status: 'Paid', link: '/invoice/customer' },
  { ref: 'FA2602-0095', third: 'WONG CHING YEE', date: '02/13/2026', due: '02/14/2026', amtExcl: '1,000.00', amtInc: '1,000.00', paid: '1,000.00', remain: '0.00', status: 'Paid', link: '#' },
  { ref: 'FA2602-0094', third: 'ALISON TAM', date: '02/10/2026', due: '02/10/2026', amtExcl: '100.00', amtInc: '100.00', paid: '100.00', remain: '0.00', status: 'Paid', link: '#' },
  { ref: 'FA2602-0093', third: 'SHEREE', date: '02/06/2026', due: '02/06/2026', amtExcl: '250.00', amtInc: '250.00', paid: '250.00', remain: '0.00', status: 'Paid', link: '#' },
  { ref: 'FA2601-0092', third: 'WONG KI', date: '01/19/2026', due: '01/19/2026', amtExcl: '6,800.00', amtInc: '6,800.00', paid: '6,800.00', remain: '0.00', status: 'Paid', link: '#' },
  { ref: 'FA2601-0091', third: 'WONG CHING YEE', date: '01/19/2026', due: '01/19/2026', amtExcl: '12,000.00', amtInc: '12,000.00', paid: '12,000.00', remain: '0.00', status: 'Paid', link: '#' },
];

export default function SalesRecords() {
  const [data, setData] = useLocalState('sales_data_v4', initialData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [selectedYear, setSelectedYear] = useState('All');
  const [itemsPerPage, setItemsPerPage] = useState(20); // State for limiting displayed rows

  const handleAdd = (newData) => {
    setData([{ ...newData, status: newData.status || 'Draft', link: '#' }, ...data]);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const handleStatusChange = (rowToUpdate) => {
    const nextStatus = getNextStatus(rowToUpdate.status, ['Draft', 'Validated', 'Paid']);
    setData(data.map(row => row === rowToUpdate ? { ...row, status: nextStatus } : row));
  };

  const sortedData = sortData(data, sortConfig);
  const filteredData = sortedData.filter(row => selectedYear === 'All' || row.date.includes(selectedYear));
  
  // Limit the displayed data based on itemsPerPage
  const displayedData = itemsPerPage === 'All' ? filteredData : filteredData.slice(0, itemsPerPage);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 ml-1">▲</span>;
    return <span className="text-[10px] text-black ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <div className="flex h-full font-sans overflow-hidden bg-white">
      <SalesSidebar />
      <div className="flex-1 flex flex-col bg-[#a6e4e7] p-2">
        <div className="flex justify-between items-center mb-3 px-1 text-gray-800">
          <div className="flex items-center space-x-2 text-gray-800">
            <FiFileText className="text-xl opacity-70 text-orange-600" />
            <span className="font-bold text-[13px] text-teal-700">List of Customer Invoices ({filteredData.length})</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(e.target.value)}
              className="border border-gray-300 bg-white py-0.5 px-2 text-[11px] rounded-sm outline-none shadow-sm cursor-pointer font-medium text-gray-700"
            >
              <option value="All">All Years</option>
              <option value="2026">Year: 2026</option>
              <option value="2025">Year: 2025</option>
            </select>
            
            <select 
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value))}
              className="border border-gray-300 bg-white py-0.5 px-1 text-[11px] rounded-sm outline-none cursor-pointer font-medium text-gray-700"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value="All">All</option>
            </select>
            
            <button onClick={() => setIsModalOpen(true)} className="bg-[#0000a0] text-white p-1 rounded shadow-sm hover:bg-blue-900 transition-colors">
              <FiPlus className="text-sm" />
            </button>
            <button className="bg-gray-100 border border-gray-300 p-1 rounded text-gray-600 hover:bg-gray-200">
              <FiPrinter className="text-sm" />
            </button>
            <button className="bg-gray-100 border border-gray-300 p-1 rounded text-gray-600 hover:bg-gray-200">
              <FiDownload className="text-sm" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-x-auto bg-white border border-black shadow-sm">
          <table className="min-w-full border-collapse text-[11px]">
            <thead>
              <tr className="bg-[#a6e4e7] text-teal-900 select-none">
                <th onClick={() => handleSort('ref')} className="p-2 border border-black font-normal text-left cursor-pointer hover:bg-teal-200 group">Ref. <SortIcon column="ref" /></th>
                <th onClick={() => handleSort('third')} className="p-2 border border-black font-normal text-left cursor-pointer hover:bg-teal-200 group">Third-party <SortIcon column="third" /></th>
                <th onClick={() => handleSort('date')} className="p-2 border border-black font-normal text-left cursor-pointer hover:bg-teal-200 group">Date <SortIcon column="date" /></th>
                <th onClick={() => handleSort('due')} className="p-2 border border-black font-normal text-left cursor-pointer hover:bg-teal-200 group">Payment due on <SortIcon column="due" /></th>
                <th onClick={() => handleSort('amtExcl')} className="p-2 border border-black font-normal text-right cursor-pointer hover:bg-teal-200 group">Amount (excl.) <SortIcon column="amtExcl" /></th>
                <th onClick={() => handleSort('amtInc')} className="p-2 border border-black font-normal text-right cursor-pointer hover:bg-teal-200 group">Amount (inc. tax) <SortIcon column="amtInc" /></th>
                <th onClick={() => handleSort('paid')} className="p-2 border border-black font-normal text-right cursor-pointer hover:bg-teal-200 group">Already paid <SortIcon column="paid" /></th>
                <th onClick={() => handleSort('remain')} className="p-2 border border-black font-normal text-right cursor-pointer hover:bg-teal-200 group">Remaining unpaid <SortIcon column="remain" /></th>
                <th onClick={() => handleSort('status')} className="p-2 border border-black font-normal text-center cursor-pointer hover:bg-teal-200 group">Status <SortIcon column="status" /></th>
              </tr>
            </thead>
            <tbody>
              {displayedData.map((row, idx) => (
                <tr key={idx} className="hover:bg-[#e6f7ff] border-b border-gray-200 transition-colors">
                  <td className="p-2 border border-black font-bold">
                    <Link to={row.link} className="text-[#008000] hover:underline flex items-center">
                      <FiFileText className="mr-1 text-gray-400" />
                      {row.ref}
                    </Link>
                  </td>
                  <td className="p-2 border border-black text-[#0000ee] hover:underline cursor-pointer">🏢 {row.third}</td>
                  <td className="p-2 border border-black text-gray-700">{row.date}</td>
                  <td className="p-2 border border-black text-gray-700">{row.due}</td>
                  <td className="p-2 border border-black text-right text-gray-700 font-medium">{row.amtExcl}</td>
                  <td className="p-2 border border-black text-right text-gray-900 font-bold">{row.amtInc}</td>
                  <td className="p-2 border border-black text-right text-[#008000] font-medium">{row.paid}</td>
                  <td className="p-2 border border-black text-right text-[#ff0000] font-bold">{row.remain}</td>
                  <td className="p-2 border border-black text-center">
                    <span onClick={() => handleStatusChange(row)} className={`cursor-pointer px-2 py-0.5 rounded-[3px] text-[10px] font-bold border transition-colors ${row.status === 'Paid' ? 'bg-[#e6f8e6] text-[#008000] border-[#b7eb8f]' : row.status === 'Validated' ? 'bg-[#fffbe6] text-[#faad14] border-[#ffe58f]' : 'bg-white text-gray-400 italic border-gray-200'}`}>
                      {row.status.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
              {displayedData.length === 0 && (
                <tr>
                  <td colSpan="9" className="p-8 text-center text-gray-500 italic">
                    No customer invoices found for {selectedYear !== 'All' ? selectedYear : 'the selected period'}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <GenericAddModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onAdd={handleAdd}
          title="Add Customer Invoice"
          fields={[
            { name: 'ref', label: 'Reference' },
            { name: 'third', label: 'Third-party' },
            { name: 'date', label: 'Date (MM/DD/YYYY)' },
            { name: 'due', label: 'Payment due on (MM/DD/YYYY)' },
            { name: 'amtInc', label: 'Amount (inc. tax)' },
            { name: 'remain', label: 'Remaining unpaid' }
          ]}
        />
      </div>
    </div>
  );
}